import { create } from 'zustand';
import { devtools, persist, createJSONStorage } from 'zustand/middleware';
import { useShallow } from 'zustand/react/shallow';
import type { AnswersMap, QuizQuestion } from '@/types';

interface CompetitionState {
  questions: QuizQuestion[];
  answers: AnswersMap;
  /** True after successful /weryfikacja or /sesja restore */
  sessionReady: boolean;
  /** Stage number of the current session (1 or 2) */
  stage: number;
  /**
   * Difference between server clock and local clock (ms).
   * Corrected server time = Date.now() + serverTimeOffset.
   * Stored after /weryfikacja to prevent clock-manipulation cheating.
   */
  serverTimeOffset: number;
  /** Unix ms – competition start received from the server */
  startTime: number;
  /** Unix ms – absolute deadline = startTime + durationMs */
  deadlineMs: number;
  /** True after the student clicks "Rozpocznij test" or resumes an IN_PROGRESS session */
  testStarted: boolean;
  /** Display index of the question the student is currently viewing (0-based) */
  currentQuestionIndex: number;
}

interface CompetitionActions {
  /**
   * Called once after /weryfikacja or /sesja responds.
   * savedAnswers restores progress when a student resumes an IN_PROGRESS session.
   */
  setVerification: (
    serverNow: number,
    startTime: number,
    durationMs?: number,
    savedAnswers?: AnswersMap,
    inProgress?: boolean,
    stage?: number,
  ) => void;
  markTestStarted: () => void;
  setQuestions: (questions: QuizQuestion[]) => void;
  setAnswer: (questionId: string, optionId: string) => void;
  setCurrentQuestionIndex: (index: number) => void;
  reset: () => void;
}

type CompetitionStore = CompetitionState & CompetitionActions;

const FALLBACK_DURATION_MS = 60 * 60 * 1000; // 60 minutes

const initialState: CompetitionState = {
  questions: [],
  answers: {},
  sessionReady: false,
  stage: 1,
  serverTimeOffset: 0,
  startTime: 0,
  deadlineMs: 0,
  testStarted: false,
  currentQuestionIndex: 0,
};

export const useCompetitionStore = create<CompetitionStore>()(
  devtools(
    persist(
      (set) => ({
        ...initialState,

        setVerification: (serverNow, startTime, durationMs = FALLBACK_DURATION_MS, savedAnswers, inProgress = false, stage = 1) =>
          set(
            {
              sessionReady: true,
              stage,
              serverTimeOffset: serverNow - Date.now(),
              startTime,
              deadlineMs: startTime + durationMs,
              answers: savedAnswers ?? {},
              testStarted: inProgress,
              currentQuestionIndex: 0,
            },
            false,
            'competition/setVerification',
          ),

        markTestStarted: () =>
          set({ testStarted: true }, false, 'competition/markTestStarted'),

        setQuestions: (questions) =>
          set({ questions }, false, 'competition/setQuestions'),

        setAnswer: (questionId, optionId) =>
          set(
            (state) => ({ answers: { ...state.answers, [questionId]: optionId } }),
            false,
            'competition/setAnswer',
          ),

        setCurrentQuestionIndex: (index) =>
          set({ currentQuestionIndex: index }, false, 'competition/setCurrentQuestionIndex'),

        reset: () => set(initialState, false, 'competition/reset'),
      }),
      {
        name: 'competition-store',
        storage: createJSONStorage(() => sessionStorage),
        partialize: (state) => ({
          sessionReady: state.sessionReady,
          stage: state.stage,
          serverTimeOffset: state.serverTimeOffset,
          startTime: state.startTime,
          deadlineMs: state.deadlineMs,
          testStarted: state.testStarted,
          answers: state.answers,
          currentQuestionIndex: state.currentQuestionIndex,
        }),
      },
    ),
    { name: 'CompetitionStore', enabled: import.meta.env.DEV },
  ),
);

export const useQuizQuestions = () =>
  useCompetitionStore((s) => s.questions);

export const useQuizAnswers = () =>
  useCompetitionStore((s) => s.answers);

export const useServerTime = () =>
  useCompetitionStore(
    useShallow((s) => ({
      serverTimeOffset: s.serverTimeOffset,
      startTime: s.startTime,
      deadlineMs: s.deadlineMs,
    })),
  );
